import { OrderStatus } from "@/common/enum/order-status";
import {
  PaymentStatus,
  paymentStatusDisplayNames,
} from "@/common/enum/payment-status";
import {
  PaymentType,
  paymentTypeDisplayNames,
} from "@/common/enum/payment-type";
import { formatDateToYYYYMMDDhhmm9 } from "@/common/util/date-util";
import { formatCommasNumber } from "@/common/util/format-number-with-commas";
import { getBankName } from "@/common/util/get-display-bank";
import { FileCopyOutlineIcon } from "@/components/icon/FileCopyOutlineIcon";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { Body14Strong } from "@/components/typography/Body14Strong";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { PaymentDto } from "@/types/dto/payment-dto";
import { Table } from "flowbite-react";
import { CustomCell } from "../CustomCell";

interface Props {
  orderDetail: OrderDetailDto;
}

export const AdminOrderPaymentTableRow: React.FC<Props> = ({ orderDetail }) => {
  const payment = orderDetail.payment;
  const isTaxProcessed = orderDetail.status === OrderStatus.TAX_PROCESSED;
  const isPayLater = orderDetail.payment.paymentMethod === PaymentType.PALT;
  const isVirtualPay = orderDetail.payment.paymentMethod === PaymentType.PAVC;

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(getAccountInfo(payment));
      window.confirm("계좌번호가 복사되었습니다.");
    } catch (error) {
      console.error("Failed to copy: ", error);
      window.confirm("계좌번호 복사에 실패했습니다.");
    }
  };

  const getAccountInfo = (payment: PaymentDto) => {
    switch (payment.paymentMethod) {
      case PaymentType.PALT: // Pay Later
        return "신한은행 140-014-327272 아카라라이프(주)";
      case PaymentType.PAVC: // Virtual Account
        return `${getBankName(payment.extraDataVC?.bankcode.slice(-2)) ?? ""} ${
          payment.extraDataVC?.account
        }`;
      case PaymentType.PABK: // Bank Transfer
        return `${getBankName(payment.extraDataDeposit?.bank_code)} ${
          payment.extraDataDeposit?.account
        }`;
      default:
        return "";
    }
  };

  const handleClickInvoiceButton = () => {
    const paymentMethod = orderDetail.payment.paymentMethod; // 결제 방식
    const paymentTno = orderDetail.payment.tno; // NHN KCP 거래번호
    const orderId = orderDetail.invoiceNumber; // 주문번호
    const amount = orderDetail.payment.amount; // 거래금액

    // 신용카드
    if (paymentMethod === "PACA") {
      window.open(
        `https://partner.kcp.co.kr/bill/card?cmd=card_bill&C_TRADE_NO=${paymentTno}&ORDER_NO=${orderId}&TRADE_MONY=${amount}`,
        "noopener,noreferrer"
      );
    }
    // 계좌이체
    if (paymentMethod === "PABK") {
      window.open(
        `https://partner.kcp.co.kr/bill/acnt?cmd=acnt_bill&A_TRADE_NO=${paymentTno}&ORDER_NO=${orderId}&TRADE_MONY=${amount}`,
        "noopener,noreferrer"
      );
    }
    // 가상계좌
    if (paymentMethod === "PAVC") {
      window.open(
        `https://partner.kcp.co.kr/bill/vcnt?cmd=vcnt_bill&V_TRADE_NO=${paymentTno}&ORDER_NO=${orderId}&TRADE_MONY=${amount}`,
        "noopener,noreferrer"
      );
    }
  };

  return (
    <Table.Row className="bg-white text-semantic-text-body">
      <Table.Cell>
        <div className="flex flex-col gap-[4px] p-[16px]">
          <Body14Regular>
            {paymentTypeDisplayNames[payment.paymentMethod]}
          </Body14Regular>
          {((isPayLater && isTaxProcessed) || isVirtualPay) && (
            <div
              className="flex gap-[4px] items-center cursor-pointer"
              onClick={handleCopyClick}
            >
              <Body14Strong>입금계좌</Body14Strong>
              <Body14Regular>{getAccountInfo(payment)}</Body14Regular>
              <FileCopyOutlineIcon
                size={16}
                color="text-grayscale-contrast-60"
              />
            </div>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="border-l">
        <CustomCell>
          <Body14Regular>{`${formatCommasNumber(
            payment.amount
          )}원`}</Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell className="border-l">
        <div className="flex flex-col justify-center p-[16px]">
          {isPayLater && isTaxProcessed ? (
            <Body14Regular className={"text-semantic-text-error-primary"}>
              세금계산서 발행 완료
            </Body14Regular>
          ) : (
            <>
              <div className="flex items-center">
                <Body14Regular
                  className={
                    payment.status === PaymentStatus.PENDING
                      ? "text-semantic-text-error-primary"
                      : "text-semantic-text-body"
                  }
                >
                  {paymentStatusDisplayNames[payment.status]}
                </Body14Regular>
                {payment.status === PaymentStatus.COMPLETED && (
                  <div className="ml-auto">
                    <div onClick={handleClickInvoiceButton}>
                      <Body14Strong className="text-semantic-link cursor-pointer">
                        매출전표 보기
                      </Body14Strong>
                    </div>
                  </div>
                )}
              </div>

              {payment.status === PaymentStatus.COMPLETED &&
                payment.completedAt && (
                  <Body14Regular className="text-semantic-text-tertiary">
                    {formatDateToYYYYMMDDhhmm9(payment.completedAt)}
                  </Body14Regular>
                )}
            </>
          )}
        </div>
      </Table.Cell>
      {isPayLater && (
        <Table.Cell className="border-l">
          <CustomCell className="flex flex-col justify-center">
            <Body14Regular className="text-semantic-text-body">
              {isTaxProcessed ? orderDetail.NO_ISS : "발행 전"}
            </Body14Regular>
          </CustomCell>
        </Table.Cell>
      )}
    </Table.Row>
  );
};
